// CARO. Casa de estilo — packs: price, name and availability (docs/plan-packs.md).
//
// A pack = N products with quantities and a % discount. Its price is never
// stored: the Worker computes it here from the components loaded fresh from D1
// (the browser only shows what the API returns). Shared (Worker, prerender,
// client display), pure: no DOM / Worker globals, `now` is explicit.

import type { Locale, Pack, PackItem, Product, ProductCategory } from '@/types';
import { isLive } from '@/lib/catalog/availability';
import { reservedForProduct } from '@/lib/holds';
import { lineKey } from '@/lib/cart/line-key';
import { productSizes, sizeUnits } from '@/lib/cart/sizes';

/** Logical path of a pack page (`/shop/pack/<slug>`; locale prefix added by localizedPath / the router). */
export const PACK_PATH = '/shop/pack';

/** Most packs of one kind in a cart line (client cap); also the most units of a product in a pack. */
export const MAX_PACK_QUANTITY = 20;

type PackPricingFields = Pick<Pack, 'discountPercent' | 'items'>;
type PackNameFields = Pick<Pack, 'discountPercent' | 'primaryCategory' | 'seq'>;

function indexById(products: readonly Product[]): Map<string, Product> {
  return new Map(products.map((p) => [p.id, p]));
}

/**
 * The largest x,99 price not above `cents` (8730 → 8699; 8699 stays 8699).
 * TS twin of the formula in scripts/lib/price-check-core.mjs (targetPriceCents),
 * which keeps its own copy for the CLI: the ONLY x,99 helper in `src/`.
 * Below 0,99 € there is no x,99 to go down to, so the amount is kept as is
 * (never negative, never above the input).
 */
export function floorTo99(cents: number): number {
  const c = Math.max(0, Math.floor(cents));
  if (c < 99) return c;
  return c - ((c % 100) + 1) % 100;
}

/**
 * Pack price: `sumCents` = Σ component price × quantity («antes»); `priceCents`
 * = that sum minus the discount, cents floored (so the real discount is at
 * least the %), then down to x,99 (`floorTo99`). Never above the plain
 * discounted price, never negative. A missing component counts 0 (such a pack
 * is not available anyway, see `packAvailability`).
 */
export function packPricing(
  pack: PackPricingFields,
  products: readonly Product[],
): { sumCents: number; priceCents: number } {
  const byId = indexById(products);
  let sumCents = 0;
  for (const item of pack.items) {
    const p = byId.get(item.productId);
    if (p) sumCents += p.priceCents * item.quantity;
  }
  const discounted = Math.floor((sumCents * (100 - pack.discountPercent)) / 100);
  return { sumCents, priceCents: floorTo99(discounted) };
}

/** Distinct stores of the pack's components, in item order (a mixed pack shows in each). */
export function packCategories(
  pack: Pick<Pack, 'items'>,
  products: readonly Product[],
): ProductCategory[] {
  const byId = indexById(products);
  const out: ProductCategory[] = [];
  for (const item of pack.items) {
    const c = byId.get(item.productId)?.category;
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/**
 * The pack's main store (default name + its `seq`): the category with most
 * units; on a tie, the tied category that appears first in item order (the
 * first component's when it is among them). null when no component is known.
 */
export function primaryCategoryOf(
  items: readonly PackItem[],
  products: readonly Product[],
): ProductCategory | null {
  const byId = indexById(products);
  const units = new Map<ProductCategory, number>(); // insertion order = item order
  for (const item of items) {
    const c = byId.get(item.productId)?.category;
    if (c) units.set(c, (units.get(c) ?? 0) + item.quantity);
  }
  let best: ProductCategory | null = null;
  let bestUnits = 0;
  for (const [c, n] of units) {
    if (n > bestUnits) {
      best = c;
      bestUnits = n;
    }
  }
  return best;
}

/**
 * Default name of an unnamed pack: ES «Pack 10% Belleza n1», EN «Pack 10%
 * Beauty no. 1», FR «Pack 10 % Beauté n° 1» (narrow no-break space U+202F
 * before % in French). `categoryLabel` gives the store name in `locale`
 * (the dictionary's `nav.<category>`).
 */
export function packDefaultName(
  pack: PackNameFields,
  locale: Locale,
  categoryLabel: (category: ProductCategory) => string,
): string {
  const cat = categoryLabel(pack.primaryCategory);
  const pct = pack.discountPercent;
  if (locale === 'en') return `Pack ${pct}% ${cat} no. ${pack.seq}`;
  if (locale === 'fr') return `Pack ${pct} % ${cat} n° ${pack.seq}`;
  return `Pack ${pct}% ${cat} n${pack.seq}`;
}

/**
 * Name shown for a pack in `locale`: its translation (`i18n[locale].name`), else
 * the base (Spanish) name, else the default name (`packDefaultName`).
 */
export function packDisplayName(
  pack: PackNameFields & Pick<Pack, 'name' | 'i18n'>,
  locale: Locale,
  categoryLabel: (category: ProductCategory) => string,
): string {
  const translated = locale === 'es' ? undefined : pack.i18n[locale]?.name?.trim();
  if (translated) return translated;
  const base = pack.name?.trim();
  if (base) return base;
  return packDefaultName(pack, locale, categoryLabel);
}

/**
 * Whether the pack can be bought now, with the reasons it can't (for /admin).
 * Available iff the pack is `active`, has components, and each one exists, is
 * `active`, live (`isLive`), bought directly (not `enquiry`) and has stock for
 * its quantity: pre-order (`metadata.preorder`) always counts; otherwise stock
 * minus units reserved by other carts (`reserved`, as priceCart) ≥ quantity,
 * and a product tracked per size needs SOME size covering the quantity (the
 * buyer picks one size per product). Reasons: `pack_not_active`, `empty`, or
 * `<code>:<productId>` with code `not_found` | `not_active` | `not_live` |
 * `enquiry` | `no_price` | `out_of_stock` | `size_out_of_stock`. A component
 * without a price (`priceCents` ≤ 0, «Precio por confirmar») is `no_price`: the
 * PDP cannot sell it, so neither can a pack.
 */
export function packAvailability(
  pack: Pick<Pack, 'status' | 'items'>,
  products: readonly Product[],
  now: number,
  reserved?: Map<string, number>,
): { available: boolean; reasons: string[] } {
  const reasons: string[] = [];
  if (pack.status !== 'active') reasons.push('pack_not_active');
  if (pack.items.length === 0) reasons.push('empty');
  const byId = indexById(products);
  for (const item of pack.items) {
    const reason = componentProblem(byId.get(item.productId), item, now, reserved);
    if (reason) reasons.push(`${reason}:${item.productId}`);
  }
  return { available: reasons.length === 0, reasons };
}

function componentProblem(
  p: Product | undefined,
  item: PackItem,
  now: number,
  reserved: Map<string, number> | undefined,
): string | null {
  if (!p) return 'not_found';
  if (p.checkoutMode === 'enquiry') return 'enquiry';
  if (p.status !== 'active') return 'not_active';
  if (!isLive(p, now)) return 'not_live';
  if (p.priceCents <= 0) return 'no_price';
  if (p.metadata.preorder === true) return null;
  if (unitsAvailable(p, reserved) < item.quantity) return 'out_of_stock';
  const best = bestSizeUnits(p, reserved);
  if (best !== null && best < item.quantity) return 'size_out_of_stock';
  return null;
}

/** Product units on hand minus those reserved by other carts (never negative). */
function unitsAvailable(p: Product, reserved: Map<string, number> | undefined): number {
  return reserved
    ? Math.max(0, p.stockQuantity - reservedForProduct(reserved, p.id))
    : p.stockQuantity;
}

/**
 * Units of the best-stocked size (minus that size's reservations), or null when
 * the product is unsized or doesn't track stock per size (only the total applies).
 */
function bestSizeUnits(p: Product, reserved: Map<string, number> | undefined): number | null {
  let best: number | null = null;
  for (const size of productSizes(p)) {
    const raw = sizeUnits(p, size);
    if (raw === null) continue;
    const units = reserved ? Math.max(0, raw - (reserved.get(lineKey(p.id, size)) ?? 0)) : raw;
    best = best === null ? units : Math.max(best, units);
  }
  return best;
}

/**
 * Most packs of this kind a cart line may hold (client cap, the server still
 * checks): min over the non-pre-order components of ⌊units / quantity⌋ (a
 * product tracked per size: its best size, also capped by the total), capped at
 * MAX_PACK_QUANTITY. All pre-order → MAX_PACK_QUANTITY; a missing component
 * or an empty pack → 0.
 */
export function maxPacks(pack: Pick<Pack, 'items'>, products: readonly Product[]): number {
  if (pack.items.length === 0) return 0;
  const byId = indexById(products);
  let max = MAX_PACK_QUANTITY;
  for (const item of pack.items) {
    const p = byId.get(item.productId);
    if (!p) return 0;
    if (p.metadata.preorder === true) continue;
    const best = bestSizeUnits(p, undefined);
    const units = Math.max(0, best === null ? p.stockQuantity : Math.min(p.stockQuantity, best));
    max = Math.min(max, Math.floor(units / item.quantity));
  }
  return max;
}

/**
 * `maxPacks` for a cart line with `sizes` chosen (productId → size): each sized,
 * non-pre-order component is also capped by the chosen size's units (capped by
 * its total) ÷ its quantity. Client cap only; the server re-checks everything.
 */
export function maxPacksForSizes(
  pack: Pick<Pack, 'items'>,
  products: readonly Product[],
  sizes: Readonly<Record<string, string>> = {},
): number {
  let max = maxPacks(pack, products);
  const byId = indexById(products);
  for (const item of pack.items) {
    const p = byId.get(item.productId);
    const size = Object.prototype.hasOwnProperty.call(sizes, item.productId) ? sizes[item.productId] : undefined;
    if (!p || !size || p.metadata.preorder === true) continue;
    const units = sizeUnits(p, size);
    const available = Math.max(0, units === null ? p.stockQuantity : Math.min(p.stockQuantity, units));
    max = Math.min(max, Math.floor(available / item.quantity));
  }
  return max;
}

/** The pack's description in `locale`: its translation, else the base (Spanish) text; '' when none. */
export function packDescription(pack: Pick<Pack, 'description' | 'i18n'>, locale: Locale): string {
  const translated = locale === 'es' ? undefined : pack.i18n[locale]?.description?.trim();
  return translated || pack.description?.trim() || '';
}

/**
 * Spreads a pack price over its expanded lines (one per component) so they sum
 * to `targetCents` EXACTLY: each line gets ⌊lineCents × target / total⌋ and the
 * leftover cents go to the most expensive line (the first one on a tie). With
 * all lines at 0 the whole target goes to the first line. Keys are returned in
 * input order.
 */
export function allocatePackDiscount(
  lines: readonly { key: string; lineCents: number }[],
  targetCents: number,
): Map<string, number> {
  const out = new Map<string, number>();
  if (lines.length === 0) return out;
  const total = lines.reduce((n, l) => n + l.lineCents, 0);
  let allocated = 0;
  let top = lines[0]!;
  for (const l of lines) {
    const share = total > 0 ? Math.floor((l.lineCents * targetCents) / total) : 0;
    out.set(l.key, share);
    allocated += share;
    if (l.lineCents > top.lineCents) top = l;
  }
  out.set(top.key, out.get(top.key)! + (targetCents - allocated));
  return out;
}

/**
 * Store names per locale (mirror of the dictionary's `nav.<category>`, kept in
 * sync by tests/cart.test.ts): shared code (priceCart, the Worker) must not
 * import the client dictionary.
 */
export const CATEGORY_LABELS: Record<Locale, Record<ProductCategory, string>> = {
  es: { beauty: 'Belleza', estilo: 'Estilo', casa: 'Casa' },
  en: { beauty: 'Beauty', estilo: 'Style', casa: 'Casa' },
  fr: { beauty: 'Beauté', estilo: 'Style', casa: 'Casa' },
};

/** Spanish store names (`CATEGORY_LABELS.es`). */
export const CATEGORY_LABEL_ES: Record<ProductCategory, string> = CATEGORY_LABELS.es;

/**
 * The pack's display name in `locale` with the shared store labels: what an
 * order line stores (`order_items.pack_name`, in the order's locale).
 */
export function packName(pack: PackNameFields & Pick<Pack, 'name' | 'i18n'>, locale: Locale): string {
  const labels = CATEGORY_LABELS[locale] ?? CATEGORY_LABELS.es;
  return packDisplayName(pack, locale, (c) => labels[c]);
}

/** The pack's Spanish display name (`packName(pack, 'es')`). */
export function packNameEs(pack: PackNameFields & Pick<Pack, 'name' | 'i18n'>): string {
  return packName(pack, 'es');
}

/** Order lines grouped for display: a loose line alone (`pack` null), or the consecutive lines of one pack. */
export interface PackItemGroup<T> {
  pack: { id: string; name: string } | null;
  items: T[];
}

/**
 * Groups CONSECUTIVE order lines that share a `packId` (priceCart writes a
 * pack's components one after the other, and orders read back in insertion
 * order). Loose lines (no `packId`) each form their own group, in order.
 */
export function groupPackItems<T extends { packId?: string | null; packName?: string | null }>(
  items: readonly T[],
): PackItemGroup<T>[] {
  const out: PackItemGroup<T>[] = [];
  for (const item of items) {
    const last = out[out.length - 1];
    if (item.packId && last?.pack?.id === item.packId) {
      last.items.push(item);
      continue;
    }
    out.push({
      pack: item.packId ? { id: item.packId, name: item.packName?.trim() || 'Pack' } : null,
      items: [item],
    });
  }
  return out;
}
